import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import ConfirmAction from '../../components/common/ConfirmAction';

const STATUS_BADGE = {
  pending: { bg: '#fef3c7', color: '#92400e', label: 'Pending' },
  documentsSubmitted: { bg: '#dbeafe', color: '#1e40af', label: 'Documents Submitted' },
  underReview: { bg: '#dbeafe', color: '#1e40af', label: 'Under Review' },
  approved: { bg: '#d1fae5', color: '#065f46', label: 'Approved' },
  rejected: { bg: '#fee2e2', color: '#991b1b', label: 'Rejected' },
  changesRequested: { bg: '#fef3c7', color: '#92400e', label: 'Changes Requested' },
};

const DOCUMENT_LABELS = {
  licenceFront: 'Driver Licence (Front)',
  licenceBack: 'Driver Licence (Back)',
  proofOfIdentity: 'Proof of Identity',
  driverAuthority: 'Licence copy (PT Code)',
  wwccDocument: 'WWCC Certificate',
  ctpInsurance: 'CTP Insurance',
  comprehensiveInsurance: 'Comprehensive Insurance',
  vehicleRego: 'Vehicle Registration',
};

// Fixed display order so related documents sit together (licence front/back, then PT-code
// copy, identity, police check, WWCC, insurances, registration).
const DOC_ORDER = ['licenceFront', 'licenceBack', 'driverAuthority', 'proofOfIdentity', 'policeCheck', 'wwccDocument', 'ctpInsurance', 'comprehensiveInsurance', 'vehicleRego'];

// Items an admin can flag for changes — keys match the driver-side update screen.
const FLAGGABLE_ITEMS = [
  { key: 'licenceFront', label: 'Driver Licence (Front)' },
  { key: 'licenceBack', label: 'Driver Licence (Back)' },
  { key: 'proofOfIdentity', label: 'Proof of Identity' },
  { key: 'policeCheck', label: 'National Police Check' },
  { key: 'driverAuthority', label: 'Licence copy (PT Code)' },
  { key: 'wwccDocument', label: 'WWCC Certificate' },
  { key: 'ctpInsurance', label: 'CTP Insurance' },
  { key: 'comprehensiveInsurance', label: 'Comprehensive Insurance' },
  { key: 'vehicleRego', label: 'Vehicle Registration' },
  { key: 'licenceNumber', label: 'Licence Number' },
  { key: 'licenceExpiry', label: 'Licence Expiry' },
  { key: 'policeCheckIssueDate', label: 'Police Check Issue Date' },
];

export default function DriverApplicationDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(null); // 'approve' | 'reject' | 'changes'
  const [reason, setReason] = useState('');
  const [changeFields, setChangeFields] = useState([]);

  const loadDetail = async () => {
    setLoading(true);
    try {
      const fn = httpsCallable(functions, 'getApplicationDetail');
      const result = await fn({ applicationUserId: userId });
      setData(result.data);
    } catch (err) {
      toast.error(err.message || 'Failed to load application');
    }
    setLoading(false);
  };

  useEffect(() => { loadDetail(); }, [userId]);

  const handleApprove = async (notes) => {
    try {
      const fn = httpsCallable(functions, 'approveDriverApplication');
      await fn({ applicationUserId: userId, notes });
      toast.success('Application approved');
      setActioning(null);
      loadDetail();
    } catch (err) {
      toast.error(err.message || 'Approval failed');
    }
  };

  const handleReject = async () => {
    if (!reason || reason.trim().length < 10) {
      toast.error('Reason must be at least 10 characters');
      return;
    }
    try {
      const fn = httpsCallable(functions, 'rejectDriverApplication');
      await fn({ applicationUserId: userId, reason: reason.trim() });
      toast.success('Application rejected');
      setActioning(null);
      setReason('');
      loadDetail();
    } catch (err) {
      toast.error(err.message || 'Rejection failed');
    }
  };

  const handleRequestChanges = async () => {
    if (!reason || reason.trim().length < 10) {
      toast.error('Description must be at least 10 characters');
      return;
    }
    try {
      const fn = httpsCallable(functions, 'requestApplicationChanges');
      await fn({ applicationUserId: userId, changesRequested: reason.trim(), fields: changeFields });
      toast.success('Changes requested — applicant will be notified');
      setActioning(null);
      setReason('');
      setChangeFields([]);
      loadDetail();
    } catch (err) {
      toast.error(err.message || 'Request failed');
    }
  };

  const toggleDocStatus = async (d) => {
    const newStatus = d.status === 'approved' ? 'pending' : 'approved';
    try {
      await updateDoc(doc(db, 'driverDocuments', d.id), { status: newStatus, reviewedAt: serverTimestamp() });
      setData((prev) => ({ ...prev, documents: prev.documents.map((x) => (x.id === d.id ? { ...x, status: newStatus } : x)) }));
    } catch (err) {
      toast.error(err.message || 'Could not update document status');
    }
  };

  if (loading) return <div className="p-10 text-center text-muted">Loading application...</div>;
  if (!data) return <div className="p-10 text-center text-muted">Application not found</div>;

  const { user, application, documents, vehicles } = data;
  const status = application?.status || 'pending';
  const badge = STATUS_BADGE[status] || STATUS_BADGE.pending;
  const isFinal = status === 'approved' || status === 'rejected';
  const currentDocs = documents.filter((d) => !d.isSuperseded);
  const pendingDocCount = currentDocs.filter((d) => d.status !== 'approved').length;
  const allDocsApproved = currentDocs.length > 0 && pendingDocCount === 0;

  return (
    <div>
      <button onClick={() => navigate('/admin/drivers')}
        className="mb-4 px-3.5 py-1.5 rounded-lg border-[1.5px] border-slate-200 bg-white cursor-pointer text-[0.85rem] font-semibold">
        ← Back to Drivers
      </button>

      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="font-serif mt-0 mb-1">
            {user.firstName} {user.lastName}
          </h1>
          <div className="text-body text-[0.9rem]">
            {user.referenceNumber || 'No ref number'} · {user.email}
          </div>
        </div>
        <span className="px-3.5 py-1.5 rounded-full font-bold text-[0.85rem]" style={{ background: badge.bg, color: badge.color }}>
          {badge.label}
        </span>
      </div>

      {/* Show rejection / changes-requested banner if applicable */}
      {status === 'rejected' && application?.rejectionReason && (
        <div className="p-3.5 rounded-xl mb-5" style={{ background: '#fef2f2', border: '1px solid #fecaca' }}>
          <div className="font-bold text-red-600 mb-1">Rejected</div>
          <div className="text-[0.85rem] text-body">{application.rejectionReason}</div>
        </div>
      )}
      {status === 'changesRequested' && application?.changesRequested && (
        <div className="p-3.5 rounded-xl mb-5" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
          <div className="font-bold mb-1" style={{ color: '#92400e' }}>Changes Requested</div>
          <div className="text-[0.85rem] text-body">{application.changesRequested}</div>
        </div>
      )}

      {/* Personal Details */}
      <Section title="Personal Details">
        <Field label="First Name" value={user.firstName} />
        <Field label="Last Name" value={user.lastName} />
        <Field label="Email" value={user.email} />
        <Field label="Phone" value={user.phone} />
        <Field label="Date of Birth" value={user.dob} />
        <Field label="WWCC Held" value={user.hasWWCC ? 'Yes' : 'No'} />
      </Section>

      {/* NSW Point to Point — PT Code */}
      <Section title="NSW Point to Point — PT Code">
        <Field label="Licence Number" value={user.licenceNumber} />
        <Field label="Licence Expiry" value={user.licenceExpiry} />
        <Field label="PT Code attested by driver" value={user.ptCodeAttested ? 'Yes' : 'No'} />
        <div className="mt-3 p-3 rounded-[10px] text-[0.82rem] leading-relaxed"
          style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e' }}>
          The driver's point-to-point credential is the PT Code applied to their NSW licence —
          there is no separate authorisation number. Before approving, verify the PT Code is
          current on the Driver Vehicle Dashboard (DVD) in the NSW Point to Point Industry Portal.
        </div>
      </Section>

      {/* Documents */}
      <Section title={`Documents (${documents.length})`}>
        {documents.length === 0 ? (
          <div className="text-muted text-[0.9rem]">No documents uploaded</div>
        ) : (
          <div className="grid gap-2">
            {[...documents].sort((a, b) => {
              const ia = DOC_ORDER.indexOf(a.type) === -1 ? 999 : DOC_ORDER.indexOf(a.type);
              const ib = DOC_ORDER.indexOf(b.type) === -1 ? 999 : DOC_ORDER.indexOf(b.type);
              if (ia !== ib) return ia - ib;
              if (a.isSuperseded !== b.isSuperseded) return a.isSuperseded ? 1 : -1;
              return 0;
            }).map((d) => (
              <div key={d.id} className={`p-3 rounded-[10px] bg-slate-50 border border-slate-200 flex justify-between items-center ${d.isSuperseded ? 'opacity-60' : ''}`}>
                <div>
                  <div className="font-semibold text-brand-dark text-[0.9rem] flex items-center gap-2">
                    {DOCUMENT_LABELS[d.type] || d.type}
                    {d.isSuperseded ? (
                      <span className="px-2 py-0.5 rounded-full text-[0.68rem] font-bold" style={{ background: '#e2e8f0', color: '#64748b' }}>Superseded</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[0.68rem] font-bold" style={{ background: '#d1fae5', color: '#065f46' }}>Current</span>
                    )}
                  </div>
                  <div className="text-[0.78rem] text-body">
                    {d.fileName} · {Math.round((d.fileSize || 0) / 1024)} KB · {d.status}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!d.isSuperseded && (
                    <button onClick={() => toggleDocStatus(d)}
                      className="px-3 py-1.5 rounded-lg border-[1.5px] cursor-pointer text-[0.8rem] font-semibold whitespace-nowrap"
                      style={d.status === 'approved'
                        ? { borderColor: '#2dd4a8', color: '#065f46', background: 'rgba(45,212,168,0.08)' }
                        : { borderColor: '#cbd5e1', color: '#64748b', background: '#fff' }}>
                      {d.status === 'approved' ? '✓ Approved' : 'Mark approved'}
                    </button>
                  )}
                  {d.downloadURL && (
                    <a href={d.downloadURL} target="_blank" rel="noopener noreferrer"
                      className="px-3.5 py-1.5 rounded-lg border-[1.5px] border-brand-primary text-emerald-600 no-underline text-[0.8rem] font-semibold">View</a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Vehicle */}
      <Section title="Vehicle">
        {vehicles.length === 0 ? (
          <div className="text-muted text-[0.9rem]">No vehicle details submitted</div>
        ) : vehicles.map((v) => (
          <div key={v.id} className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 mb-2">
            <Field label="Make / Model" value={`${v.make || '?'} ${v.model || ''}`} />
            <Field label="Year" value={v.year} />
            <Field label="Colour" value={v.colour} />
            <Field label="Plate" value={v.plate} />
            <Field label="Tier" value={v.tier} />
            <Field label="Max Passengers" value={v.maxPassengers} />
            <Field label="Status" value={v.status} />
          </div>
        ))}
      </Section>

      {/* ABN / Application Data */}
      {application && (
        <Section title="Application Data">
          {application.step5?.abn && <Field label="ABN" value={application.step5.abn} />}
          {application.step5?.entityName && <Field label="Entity Name" value={application.step5.entityName} />}
          {application.step8?.zones && (
            <Field label="Service Zones" value={
              Array.isArray(application.step8.zones) ? application.step8.zones.join(', ') : 'None'
            } />
          )}
          {application.submittedAt && (
            <Field label="Submitted" value={new Date(application.submittedAt).toLocaleString()} />
          )}
        </Section>
      )}

      {/* Action buttons */}
      {!isFinal && (
        <div className="mt-6 p-4 bg-white rounded-xl border border-slate-200">
          <h3 className="mt-0 mb-3 text-base">Review Action</h3>
          <div className="flex gap-2">
            <button
              onClick={() => {
                if (!allDocsApproved) {
                  toast.error(currentDocs.length === 0
                    ? 'No documents uploaded yet.'
                    : `Approve every document first — ${pendingDocCount} still pending.`);
                  return;
                }
                setActioning('approve');
              }}
              disabled={!allDocsApproved}
              className="px-[18px] py-2.5 rounded-lg border-0 bg-brand-primary text-white font-bold"
              style={{ opacity: allDocsApproved ? 1 : 0.5, cursor: allDocsApproved ? 'pointer' : 'not-allowed' }}>✓ Approve</button>
            <button onClick={() => { setChangeFields(documents.filter((d) => !d.isSuperseded && d.status !== 'approved').map((d) => d.type)); setActioning('changes'); }}
              className="px-[18px] py-2.5 rounded-lg border-[1.5px] bg-white cursor-pointer font-bold"
              style={{ borderColor: '#fbbf24', color: '#92400e' }}>↻ Request Changes</button>
            <button onClick={() => setActioning('reject')}
              className="px-[18px] py-2.5 rounded-lg border-[1.5px] border-red-600 bg-white text-red-600 cursor-pointer font-bold">✗ Reject</button>
          </div>
          {!allDocsApproved && (
            <div className="text-[0.8rem] text-muted mt-2">
              {currentDocs.length === 0
                ? 'Upload and approve the applicant\'s documents before approving the application.'
                : `Approve every document above before you can approve the application — ${pendingDocCount} still pending.`}
            </div>
          )}
        </div>
      )}

      {actioning === 'approve' && (
        <ConfirmAction
          title="Approve this application?"
          description={`This will activate ${user.firstName}'s driver account and their primary vehicle. They will receive a notification.`}
          confirmLabel="Approve"
          reasonRequired={false}
          destructive={false}
          onConfirm={handleApprove}
          onCancel={() => setActioning(null)}
        />
      )}

      {(actioning === 'reject' || actioning === 'changes') && (
        <div className="fixed inset-0 flex items-center justify-center z-[1000] p-5" style={{ background: 'rgba(10,22,40,0.6)' }}
          onClick={() => { setActioning(null); setReason(''); setChangeFields([]); }}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 max-w-[480px] w-full">
            <h3 className="mt-0 mb-2" style={{ color: actioning === 'reject' ? '#dc2626' : '#92400e' }}>
              {actioning === 'reject' ? '⚠ Reject Application' : '↻ Request Changes'}
            </h3>
            <p className="text-body text-[0.9rem] mb-3">
              {actioning === 'reject'
                ? 'This will permanently mark the application as rejected. The applicant will be notified.'
                : 'Describe what needs to change. The applicant will be notified and asked to update their submission.'}
            </p>
            <textarea
              rows={4}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={actioning === 'reject' ? 'Reason for rejection (10+ chars)...' : 'Describe required changes (10+ chars)...'}
              className="w-full p-2.5 rounded-lg border-[1.5px] border-slate-200 text-[0.9rem] font-[inherit] resize-y mb-3"
            />
            {actioning === 'changes' && (
              <div className="mb-3">
                <div className="text-[0.82rem] font-semibold text-brand-dark mb-2">Which items need changing? (optional — drives the driver's update screen)</div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 max-h-[180px] overflow-auto p-1">
                  {FLAGGABLE_ITEMS.map((it) => (
                    <label key={it.key} className="flex items-center gap-2 text-[0.82rem] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={changeFields.includes(it.key)}
                        onChange={(e) => setChangeFields((prev) => e.target.checked ? [...prev, it.key] : prev.filter((k) => k !== it.key))}
                      />
                      <span>{it.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setActioning(null); setReason(''); setChangeFields([]); }}
                className="px-4 py-2 rounded-lg border-[1.5px] border-slate-200 bg-white cursor-pointer font-semibold">Cancel</button>
              <button onClick={actioning === 'reject' ? handleReject : handleRequestChanges}
                disabled={reason.trim().length < 10}
                className="px-4 py-2 rounded-lg border-0 text-white font-semibold"
                style={{
                  background: reason.trim().length >= 10 ? (actioning === 'reject' ? '#dc2626' : '#fbbf24') : '#e2e8f0',
                  cursor: reason.trim().length >= 10 ? 'pointer' : 'not-allowed',
                }}>
                {actioning === 'reject' ? 'Reject' : 'Request Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helpers
function Section({ title, children }) {
  return (
    <div className="mb-5">
      <h3 className="mt-0 mb-3 text-base text-brand-dark">{title}</h3>
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        {children}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="flex justify-between py-1.5 text-[0.85rem] border-b border-slate-100">
      <span className="text-body">{label}</span>
      <span className="font-medium text-brand-dark">{value || '—'}</span>
    </div>
  );
}